import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar, FileText, Upload, Plus, Download, Ban,
  Clock, CheckCircle2, XCircle, AlertCircle, ChevronRight,
  Briefcase, Sun, TrendingUp, FolderOpen, Eye, File
} from "lucide-react";
import { formatDateES } from "@/lib/dateUtils";
import FileUploadZone from "@/components/FileUploadZone";
import { getRequestableTypes, getVisibility } from "@/lib/portalVisibility";
import { useYearRange } from "@/hooks/useYearRange";
const STATUS_ICONS = {
  en_aprobacion: Clock,
  aprobada: CheckCircle2,
  rechazada: XCircle,
  cancelada: Ban,
  comunicada: AlertCircle,
  documentacion_pendiente: FileText,
  registrada: CheckCircle2,
  cerrada: CheckCircle2,
};

export default function EmployeeDashboardPage() {
  const { axiosAuth, user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [hrConfig, setHrConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const [form, setForm] = useState({
    request_type: "", start_date: "", end_date: "", comments: "",
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setConfigLoading(true);
    setConfigError(false);
    try {
      const [balRes, reqRes] = await Promise.all([
        axiosAuth().get("/vacation-requests/balance"),
        axiosAuth().get("/vacation-requests"),
      ]);
      setBalance(balRes.data);
      setRequests(reqRes.data);
    } catch {}

    // Load config separately to track its own state
    try {
      const cfgRes = await axiosAuth().get("/hr/employee-config");
      setHrConfig(cfgRes.data);
      setConfigError(false);
    } catch {
      setHrConfig(null);
      setConfigError(true);
    } finally {
      setConfigLoading(false);
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const stateMap = {};
  (hrConfig?.leave_states || []).forEach(s => { stateMap[s.value] = s; });
  const typeMap = {};
  (hrConfig?.request_types || []).forEach(t => { typeMap[t.value] = t; });

  // Tipos que el empleado puede SOLICITAR (portal_visibility[type].allow_request !== false).
  // OCULTAR LA TARJETA NO IMPIDE SOLICITAR. Fuente única: lib/portalVisibility.js
  // Memoizado para no recrear el array en cada render (evita loops en useEffect dependientes).
  const activeTypes = useMemo(() => getRequestableTypes(hrConfig), [hrConfig]);

  // Sync default form type when activeTypes load
  useEffect(() => {
    if (activeTypes.length > 0 && !form.request_type) {
      setForm(prev => ({ ...prev, request_type: activeTypes[0].value }));
    }
  }, [activeTypes, form.request_type]);

  const selectedType = activeTypes.find(t => t.value === form.request_type);

  const filteredRequests = filter === "all"
    ? requests
    : requests.filter(r => {
        if (filter === "pending") return ["en_aprobacion"].includes(r.status);
        if (filter === "approved") return r.status === "aprobada";
        if (filter === "rejected") return ["rechazada", "cancelada"].includes(r.status);
        return true;
      });

  const submit = async () => {
    if (!form.request_type) { toast.error("Selecciona un tipo de solicitud"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Selecciona las fechas de inicio y fin"); return; }
    if (form.end_date < form.start_date) { toast.error("La fecha fin debe ser posterior a la de inicio"); return; }
    setSubmitting(true);
    try {
      // 1. Create request (inform backend if docs are attached)
      const payload = { ...form, has_initial_docs: pendingFiles.length > 0 };
      const res = await axiosAuth().post("/vacation-requests", payload);
      const reqId = res.data?.id;

      // 2. Upload attached files
      let uploadErrors = 0;
      if (reqId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            await axiosAuth().post(`/vacation-requests/${reqId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch {
            uploadErrors++;
          }
        }
      }

      if (uploadErrors > 0) {
        toast.warning(`Solicitud creada. ${uploadErrors} archivo(s) no se pudieron subir. Puedes adjuntarlos despues.`);
      } else if (pendingFiles.length > 0) {
        toast.success(`Solicitud enviada con ${pendingFiles.length} documento(s)`);
      } else {
        toast.success("Solicitud enviada correctamente");
      }
      setShowNew(false);
      setForm({ request_type: activeTypes[0]?.value || "", start_date: "", end_date: "", comments: "" });
      setPendingFiles([]);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar la solicitud"); }
    finally { setSubmitting(false); }
  };

  const cancelReq = async (id) => {
    try {
      await axiosAuth().put(`/vacation-requests/${id}/cancel`);
      toast.success("Solicitud cancelada");
      setSelected(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cancelar"); }
  };

  const openNewRequest = () => {
    if (configLoading) { toast.info("Cargando configuracion, espera un momento..."); return; }
    if (configError || activeTypes.length === 0) {
      toast.error("No se pudo cargar la configuracion de solicitudes. Reintentando...");
      loadAll();
      return;
    }
    setForm({ request_type: activeTypes[0]?.value || "", start_date: "", end_date: "", comments: "" });
    setShowNew(true);
  };

  const pendingCount = requests.filter(r => ["en_aprobacion"].includes(r.status)).length;
  const approvedCount = requests.filter(r => r.status === "aprobada").length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="employee-loading">
        <div className="h-10 w-64 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-36 bg-slate-100 animate-pulse rounded-sm" />)}
        </div>
        <div className="h-64 bg-slate-100 animate-pulse rounded-sm" />
      </div>
    );
  }

  const usedPct = balance ? Math.round((balance.used / Math.max(balance.max_days, 1)) * 100) : 0;
  const pendPct = balance ? Math.round((balance.pending / Math.max(balance.max_days, 1)) * 100) : 0;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="employee-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1">Portal Empleado</p>
          <h1 className="heading-industrial text-3xl font-bold text-slate-900 uppercase leading-none" data-testid="employee-title">
            Hola, {user?.name?.split(" ")[0] || "Empleado"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestiona tus vacaciones, permisos y ausencias</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AnnualSummaryDownload axiosAuth={axiosAuth} />
          <Button
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-6 shadow-sm"
            onClick={openNewRequest}
            data-testid="btn-new-request"
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva Solicitud
          </Button>
        </div>
      </div>

      {/* Instructions */}
      {hrConfig?.employee_instructions && (
        <div className="flex items-start gap-3 p-4 bg-slate-900 text-white rounded-sm" data-testid="employee-instructions">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <p className="text-xs leading-relaxed">{hrConfig.employee_instructions}</p>
        </div>
      )}

      {/* Balance Section - Per Type */}
      {balance && (
        <div data-testid="balance-section">
          {balance.any_incoherence && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-3 text-xs text-red-900" data-testid="balance-incoherence-warning">
              <strong>⚠ Hay una incoherencia entre solicitudes aprobadas y saldo disponible</strong>
              <span className="block mt-0.5 text-[11px]">
                Tipos afectados: {(balance.incoherent_types || []).join(", ")}. Contacta con RRHH para revisar.
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Main vacation balance card */}
            <Card className="md:col-span-2 border-slate-200 rounded-sm overflow-hidden" data-testid="balance-main-card">
              <CardContent className="p-0">
                <div className="p-5 pb-4">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-500" />
                      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Balance Vacaciones {new Date().getFullYear()}</span>
                    </div>
                    <span className="text-xs font-mono text-slate-400">{balance.max_days} dias asignados</span>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-end gap-1 mb-3">
                      <span className="text-5xl font-bold font-mono text-slate-900 leading-none" data-testid="balance-available">{balance.available}</span>
                      <span className="text-sm text-slate-400 mb-1">dias disponibles reales</span>
                    </div>
                    {/* Barra apilada: disfrutados + aprobados futuros + pendientes */}
                    <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden" data-testid="balance-progress-bar">
                      <div className="h-full flex">
                        <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${Math.round(((balance.used_past || 0) / Math.max(balance.max_days, 1)) * 100)}%` }} title={`Disfrutados: ${balance.used_past}`} />
                        <div className="bg-blue-500 transition-all duration-500" style={{ width: `${Math.round(((balance.approved_future || 0) / Math.max(balance.max_days, 1)) * 100)}%` }} title={`Aprobados futuros: ${balance.approved_future}`} />
                        <div className="bg-amber-400 transition-all duration-500" style={{ width: `${pendPct}%` }} title={`Pendientes: ${balance.pending}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3 flex-wrap" data-testid="balance-breakdown">
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[11px] text-slate-600">Disfrutados <strong data-testid="balance-used-past">{balance.used_past || 0}</strong></span></div>
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="text-[11px] text-slate-600">Aprobados futuros <strong data-testid="balance-approved-future">{balance.approved_future || 0}</strong></span></div>
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="text-[11px] text-slate-600">Pendientes <strong>{balance.pending}</strong></span></div>
                      <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-slate-200" /><span className="text-[11px] text-slate-600">Disponibles <strong>{balance.available}</strong></span></div>
                      {(balance.rejected > 0 || balance.cancelled > 0) && (
                        <div className="flex items-center gap-1.5" title="Las solicitudes rechazadas o canceladas no descuentan saldo">
                          <span className="text-[10px] text-slate-400">·</span>
                          <span className="text-[11px] text-slate-400">Rechazadas <strong>{balance.rejected || 0}</strong></span>
                          <span className="text-[10px] text-slate-400">·</span>
                          <span className="text-[11px] text-slate-400">Canceladas <strong>{balance.cancelled || 0}</strong></span>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">
                      Disponibles = asignados − disfrutados − aprobados futuros − pendientes.
                      Si se aprobaran todas las pendientes quedarían <strong data-testid="balance-available-optimistic">{balance.available_optimistic ?? balance.available}</strong> días.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick stats */}
            <div className="flex flex-col gap-4">
              <Card className="border-slate-200 rounded-sm flex-1" data-testid="stat-pending">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-amber-50 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-amber-600" /></div>
                  <div><p className="text-2xl font-bold font-mono">{pendingCount}</p><p className="text-[11px] text-slate-500">En aprobacion</p></div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm flex-1" data-testid="stat-approved">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center flex-shrink-0"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="text-2xl font-bold font-mono">{approvedCount}</p><p className="text-[11px] text-slate-500">Aprobadas</p></div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 rounded-sm flex-1" data-testid="stat-total">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center flex-shrink-0"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold font-mono">{requests.length}</p><p className="text-[11px] text-slate-500">Total solicitudes</p></div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Per-type balances */}
          {balance.by_type && Object.keys(balance.by_type).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="balance-by-type">
              {Object.entries(balance.by_type)
                .filter(([type]) => getVisibility(hrConfig, type).card !== false)
                .map(([type, bal]) => (
                <Card key={type} className="border-slate-200 rounded-sm" data-testid={`balance-card-${type}`}>
                  <CardContent className="p-3">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 truncate">{type.replace("_", " ")}</p>
                    {bal.assigned !== null ? (
                      <div className="space-y-1.5">
                        <div className="flex items-end gap-1">
                          <span className="text-xl font-bold font-mono">{bal.available}</span>
                          <span className="text-[10px] text-slate-400 mb-0.5">disp.</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.round((bal.used / Math.max(bal.assigned, 1)) * 100)}%` }} />
                        </div>
                        <div className="flex justify-between text-[9px] text-slate-400">
                          <span>Asign: {bal.assigned}</span>
                          <span>Usados: {bal.used}</span>
                          <span>Pend: {bal.pending}</span>
                        </div>
                      </div>
                    ) : (
                      <div><span className="text-xl font-bold font-mono">{bal.used}</span><span className="text-[10px] text-slate-400 ml-1">dias</span></div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mis Documentos */}
      <MyDocumentsSection axiosAuth={axiosAuth} />

      {/* Requests List */}
      <Card className="border-slate-200 rounded-sm" data-testid="requests-list">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-red-600" /> Mis Solicitudes
              <Badge variant="outline" className="text-[10px] ml-1 rounded-sm">{filteredRequests.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-1.5">
              {[
                { key: "all", label: "Todas" },
                { key: "pending", label: "Pendientes" },
                { key: "approved", label: "Aprobadas" },
                { key: "rejected", label: "Rechazadas" },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`px-3 py-1.5 text-[11px] font-medium rounded-sm transition-colors ${
                    filter === f.key
                      ? "bg-slate-900 text-white"
                      : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-200" />
              <p className="text-sm text-slate-400">
                {filter === "all" ? "No tienes solicitudes. Crea tu primera solicitud." : "No hay solicitudes con este filtro."}
              </p>
              {filter === "all" && (
                <Button variant="outline" size="sm" className="mt-4 text-xs rounded-sm" onClick={openNewRequest} data-testid="empty-new-request">
                  <Plus className="w-3 h-3 mr-1" /> Crear solicitud
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredRequests.map(r => {
                const st = stateMap[r.status] || { label: r.status, color: "#94a3b8" };
                const tp = typeMap[r.request_type] || { label: r.request_type };
                const Icon = STATUS_ICONS[r.status] || Clock;
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/80 cursor-pointer transition-colors group"
                    onClick={() => setSelected(r)}
                    data-testid={`request-${r.id}`}
                  >
                    <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: st.color + "15" }}>
                      <Icon className="w-4 h-4" style={{ color: st.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-slate-900">{tp.label}</span>
                        <Badge
                          variant="outline"
                          className="text-[10px] rounded-sm px-1.5"
                          style={{ color: st.color, borderColor: st.color + "40" }}
                        >
                          {st.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500">
                        <span className="font-mono">{formatDateES(r.start_date)} - {formatDateES(r.end_date)}</span>
                        <span className="text-slate-300">|</span>
                        <span>{r.working_days} dias lab.</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md rounded-sm" data-testid="new-request-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase tracking-wider">Nueva Solicitud</DialogTitle>
          </DialogHeader>

          {configLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="modal-config-loading">
              <div className="animate-spin h-6 w-6 border-2 border-slate-900 border-t-transparent rounded-full" />
              <p className="text-xs text-slate-500">Cargando tipos de solicitud...</p>
            </div>
          ) : configError || activeTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center" data-testid="modal-config-error">
              <AlertCircle className="w-8 h-8 text-amber-500" />
              <p className="text-sm text-slate-700 font-medium">No hay tipos de solicitud disponibles</p>
              <p className="text-xs text-slate-400 max-w-xs">Contacta con administracion para que configuren los tipos de solicitud.</p>
              <Button variant="outline" size="sm" className="text-xs rounded-sm mt-2" onClick={() => { loadAll(); }} data-testid="modal-retry-btn">
                Reintentar
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-5 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tipo de solicitud</Label>
                  <Select value={form.request_type} onValueChange={v => setForm({ ...form, request_type: v })}>
                    <SelectTrigger className="h-10 text-sm rounded-sm border-slate-200" data-testid="new-type">
                      <SelectValue placeholder="Selecciona un tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {/* Type-specific hints */}
                  {selectedType && (
                    <div className="flex flex-wrap gap-2 mt-1.5" data-testid="type-hints">
                      {selectedType.requires_docs && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">
                          <FileText className="w-3 h-3" /> Requiere documentacion
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Fecha inicio</Label>
                    <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                      className="h-10 text-sm rounded-sm border-slate-200" data-testid="new-start" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Fecha fin</Label>
                    <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                      className="h-10 text-sm rounded-sm border-slate-200" data-testid="new-end" />
                  </div>
                </div>
                {form.start_date && form.end_date && form.end_date >= form.start_date && (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-sm" data-testid="new-days-preview">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-600">
                      Periodo seleccionado: <strong className="text-slate-900">{formatDateES(form.start_date)}</strong> al <strong className="text-slate-900">{formatDateES(form.end_date)}</strong>
                    </span>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Comentarios <span className="text-slate-300">(opcional)</span></Label>
                  <Textarea value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })}
                    className="text-sm rounded-sm h-20 border-slate-200 resize-none" placeholder="Motivo o detalle adicional..." data-testid="new-comments" />
                </div>
                {/* File Upload */}
                <FileUploadZone
                  files={pendingFiles}
                  setFiles={setPendingFiles}
                  requirement={selectedType?.requires_docs ? "required" : "optional"}
                  disabled={submitting}
                />
              </div>
              <DialogFooter className="mt-2 gap-2">
                <Button variant="outline" size="sm" className="text-xs rounded-sm uppercase tracking-wider" onClick={() => { setShowNew(false); setPendingFiles([]); }}>Cancelar</Button>
                <Button
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm uppercase tracking-wider font-semibold px-6"
                  onClick={submit}
                  disabled={!form.request_type || submitting}
                  data-testid="btn-submit-request"
                >
                  {submitting ? "Enviando..." : pendingFiles.length > 0 ? `Enviar con ${pendingFiles.length} doc${pendingFiles.length > 1 ? "s" : ""}` : "Enviar Solicitud"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase tracking-wider flex items-center gap-2">
              Detalle de Solicitud
              {selected && (
                <Badge
                  variant="outline"
                  className="text-[10px] rounded-sm"
                  style={{ color: stateMap[selected.status]?.color, borderColor: stateMap[selected.status]?.color }}
                >
                  {stateMap[selected.status]?.label || selected.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5 text-sm">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Tipo</span>
                  <p className="font-semibold text-slate-900">{typeMap[selected.request_type]?.label || selected.request_type}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Dias laborables</span>
                  <p className="font-bold text-2xl font-mono text-slate-900">{selected.working_days}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Fecha inicio</span>
                  <p className="font-mono text-slate-700">{formatDateES(selected.start_date)}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Fecha fin</span>
                  <p className="font-mono text-slate-700">{formatDateES(selected.end_date)}</p>
                </div>
              </div>

              {/* Comments */}
              {selected.comments && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Comentarios</span>
                  <p className="mt-1 p-3 bg-slate-50 rounded-sm text-xs text-slate-700 leading-relaxed">{selected.comments}</p>
                </div>
              )}

              {/* Review notes */}
              {selected.review_notes && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">Observaciones del revisor</span>
                  <p className="mt-1 p-3 bg-amber-50 rounded-sm text-xs text-amber-800 leading-relaxed">{selected.review_notes}</p>
                </div>
              )}

              {/* Communication-type notice (non-approvable) */}
              {selected.requires_approval === false && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-sm" data-testid="communicated-type-notice">
                  <span className="text-[10px] uppercase font-bold text-purple-700 tracking-wider">Tipo comunicado</span>
                  <p className="text-[11px] text-purple-600 mt-1">
                    Este tipo de solicitud no requiere aprobacion. Se gestiona como comunicacion directa.
                  </p>
                </div>
              )}

              {/* Approval trail - only for approvable types */}
              {selected.requires_approval !== false && selected.approval_flow && selected.approval_flow.length > 0 && (
                <div data-testid="approval-trail">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Flujo de aprobacion</span>
                  <div className="mt-2 space-y-2">
                    {selected.approval_flow.map((s, i) => (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-sm border ${s.status === "approved" ? "bg-green-50 border-green-200" : s.status === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${s.status === "approved" ? "bg-green-500" : s.status === "rejected" ? "bg-red-500" : "bg-amber-400"}`}>
                          {s.status === "approved" ? <CheckCircle2 className="w-3 h-3 text-white" /> : s.status === "rejected" ? <XCircle className="w-3 h-3 text-white" /> : <Clock className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold">{s.label}</p>
                          {s.resolved_by_name && <p className="text-[10px] text-slate-500">{s.resolved_by_name} - {formatDateES(s.resolved_at)}</p>}
                          {s.notes && <p className="text-[10px] text-slate-600 italic">{s.notes}</p>}
                        </div>
                        <span className={`text-[9px] font-semibold ${s.status === "approved" ? "text-green-600" : s.status === "rejected" ? "text-red-600" : "text-amber-600"}`}>
                          {s.status === "approved" ? "Aprobado" : s.status === "rejected" ? "Rechazado" : "Pendiente"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents (unified from hr_documents) */}
              <RequestDocsEmployee axiosAuth={axiosAuth} requestId={selected.id} />
            </div>
          )}
          <DialogFooter className="mt-2">
            {selected && !["aprobada", "rechazada", "cancelada", "registrada", "cerrada"].includes(selected.status) && (
              <Button
                variant="destructive"
                size="sm"
                className="text-xs rounded-sm uppercase tracking-wider font-semibold"
                onClick={() => cancelReq(selected.id)}
                data-testid="btn-cancel-request"
              >
                <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancelar Solicitud
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// ANNUAL SUMMARY DOWNLOAD (PDF/Excel)
// Resumen anual de saldos y solicitudes propias.
// ═══════════════════════════════════════════
function AnnualSummaryDownload({ axiosAuth }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);
  const wrapRef = useRef(null);
  // Rango RRHH global: NO más [currentYear, -1, -2] hardcoded.
  // Fuente única: useYearRange() → /api/rrhh/year-range.
  const { yearsDesc } = useYearRange();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const download = async (format) => {
    try {
      setDownloading(true);
      const path = format === "pdf" ? "annual-summary.pdf" : "annual-summary.xlsx";
      const res = await axiosAuth().get(`/employee-portal/${path}?year=${year}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `resumen_anual_${year}.${format === "pdf" ? "pdf" : "xlsx"}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
      toast.success(`Resumen ${year} descargado`);
    } catch (e) {
      // axios con responseType=blob devuelve el error también como Blob.
      let detail = "No se pudo generar el resumen";
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || detail; } catch { detail = text || detail; }
        } else if (typeof e.response?.data?.detail === "string") {
          detail = e.response.data.detail;
        }
      } catch { /* mantiene mensaje por defecto */ }
      toast.error(detail);
    } finally { setDownloading(false); }
  };

  const currentYear = new Date().getFullYear();
  // Si el usuario tiene el año actual en su rango, lo elegimos por defecto.
  useEffect(() => {
    if (yearsDesc.length > 0 && !yearsDesc.includes(year)) {
      setYear(yearsDesc.includes(currentYear) ? currentYear : yearsDesc[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsDesc]);

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        data-testid="btn-annual-summary"
        className="h-10 rounded-sm text-xs uppercase tracking-wider font-semibold border-slate-300"
      >
        <Download className="w-4 h-4 mr-2" /> Resumen anual
      </Button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-sm shadow-lg z-50 p-3"
          data-testid="annual-summary-menu"
        >
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5" data-testid="annual-year-range-label">
            Año <span className="text-slate-400 font-normal normal-case">({yearsDesc[yearsDesc.length - 1]}–{yearsDesc[0]})</span>
          </p>
          <div className="grid grid-cols-4 gap-1 mb-3 max-h-32 overflow-y-auto" data-testid="annual-year-grid">
            {yearsDesc.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                data-testid={`annual-year-${y}`}
                className={`text-xs py-1.5 rounded-sm font-semibold border transition-colors ${
                  year === y
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Formato</p>
          <div className="space-y-1.5">
            <button
              disabled={downloading}
              onClick={() => download("pdf")}
              data-testid="annual-download-pdf"
              className="w-full flex items-center gap-2 text-xs px-3 py-2 rounded-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FileText className="w-3.5 h-3.5 text-red-600" />
              <span className="font-medium">PDF</span>
              <span className="text-slate-400 ml-auto">imprimible</span>
            </button>
            <button
              disabled={downloading}
              onClick={() => download("xlsx")}
              data-testid="annual-download-xlsx"
              className="w-full flex items-center gap-2 text-xs px-3 py-2 rounded-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FileText className="w-3.5 h-3.5 text-emerald-600" />
              <span className="font-medium">Excel</span>
              <span className="text-slate-400 ml-auto">.xlsx</span>
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
            Incluye tus saldos del año, todas tus solicitudes (vacaciones, asuntos, permisos, bajas) y el modo de cálculo con el que fue computada cada una.
          </p>
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════
// DOCUMENTS FOR A REQUEST (employee view)
// ═══════════════════════════════════════════
function RequestDocsEmployee({ axiosAuth, requestId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`/hr/documents/by-request/${requestId}`);
      setDocs(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [axiosAuth, requestId]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecciona un archivo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axiosAuth().post(`/vacation-requests/${requestId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
  };

  const download = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const preview = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      setPreviewUrl(url);
      setPreviewType(doc.content_type);
    } catch { toast.error("Vista previa no disponible"); }
  };

  const STATE_COLORS = { pendiente: "text-amber-600 border-amber-200", recibida: "text-blue-600 border-blue-200", validada: "text-green-600 border-green-200", rechazada: "text-red-600 border-red-200" };
  const STATE_LABELS = { pendiente: "Pendiente", recibida: "Recibida", validada: "Validada", rechazada: "Rechazada" };

  return (
    <div data-testid="request-docs-employee">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        Documentos ({docs.length})
      </span>
      {loading ? <p className="text-[10px] text-slate-400 mt-1">Cargando...</p> : (
        <>
          {docs.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {docs.map(d => {
                const previewable = ["application/pdf", "image/jpeg", "image/png"].includes(d.content_type);
                return (
                  <div key={d.id} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-sm border border-slate-100" data-testid={`emp-req-doc-${d.id}`}>
                    <File className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{d.filename}</p>
                      <p className="text-[9px] text-slate-400">{d.category_label || d.category} · {d.uploaded_by}</p>
                    </div>
                    <Badge variant="outline" className={`text-[8px] ${STATE_COLORS[d.state] || ""}`}>{STATE_LABELS[d.state] || d.state}</Badge>
                    {previewable && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => preview(d)} title="Vista previa">
                        <Eye className="w-3.5 h-3.5 text-blue-400" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => download(d)} title="Descargar" data-testid={`download-doc-${d.id}`}>
                      <Download className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="h-9 text-xs rounded-sm flex-1 border-slate-200" data-testid="doc-upload" />
            <Button variant="outline" size="sm" className="h-9 text-xs rounded-sm gap-1.5 border-slate-200" onClick={upload} data-testid="btn-upload-doc">
              <Upload className="w-3.5 h-3.5" /> Subir
            </Button>
          </div>
        </>
      )}
      {/* Preview Dialog */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="preview-dialog">
            <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">Vista previa</DialogTitle></DialogHeader>
            {previewType === "application/pdf" ? (
              <iframe src={previewUrl} className="w-full h-[60vh] rounded-sm" title="Preview" />
            ) : (
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[60vh] object-contain mx-auto rounded-sm" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════
// MY DOCUMENTS SECTION
// ═══════════════════════════════════════════
function MyDocumentsSection({ axiosAuth }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/hr/documents/my");
      setDocs(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const preview = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      setPreviewUrl(url);
      setPreviewType(doc.content_type);
    } catch { toast.error("Vista previa no disponible"); }
  };

  const STATE_COLORS = { pendiente: "text-amber-600 border-amber-200", recibida: "text-blue-600 border-blue-200", validada: "text-green-600 border-green-200", rechazada: "text-red-600 border-red-200" };
  const STATE_LABELS = { pendiente: "Pendiente", recibida: "Recibida", validada: "Validada", rechazada: "Rechazada" };

  if (loading) return null;
  if (docs.length === 0) return null;

  return (
    <>
      <Card className="border-slate-200 rounded-sm" data-testid="my-documents-section">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-blue-600" /> Mis Documentos
            <Badge variant="outline" className="text-[10px] ml-1 rounded-sm">{docs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {docs.map(d => {
              const previewable = ["application/pdf", "image/jpeg", "image/png"].includes(d.content_type);
              return (
                <div key={d.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/80 group" data-testid={`my-doc-${d.id}`}>
                  <File className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.filename}</p>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                      <span>{d.category_label || d.category}</span>
                      <span className="text-slate-300">|</span>
                      <span className="font-mono">{(() => { const p = d.created_at?.slice(0,10)?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d.created_at; })()}</span>
                      {d.request_id && (
                        <>
                          <span className="text-slate-300">|</span>
                          <span className="text-blue-500">Vinculado a solicitud</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${STATE_COLORS[d.state] || ""}`}>{STATE_LABELS[d.state] || d.state}</Badge>
                  {previewable && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-60 group-hover:opacity-100" onClick={() => preview(d)} title="Vista previa">
                      <Eye className="w-3.5 h-3.5 text-blue-500" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-60 group-hover:opacity-100" onClick={() => download(d)} title="Descargar" data-testid={`my-doc-download-${d.id}`}>
                    <Download className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" />
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {/* Preview Dialog */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="my-docs-preview-dialog">
            <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">Vista previa</DialogTitle></DialogHeader>
            {previewType === "application/pdf" ? (
              <iframe src={previewUrl} className="w-full h-[60vh] rounded-sm" title="Preview" />
            ) : (
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[60vh] object-contain mx-auto rounded-sm" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
